import { MapPin } from "lucide-react";
import type { Location } from "@/lib/locationContext";

export type LocationStatus = "healthy" | "at-risk" | "pre-opening";

export function getLocationStatus(stats: { inspectionPct: number; expiringSoon: number } | null): LocationStatus {
  if (!stats) return "pre-opening";
  if (stats.expiringSoon > 0 || stats.inspectionPct < 50) return "at-risk";
  if (stats.inspectionPct < 80) return "pre-opening";
  return "healthy";
}

const STATUS_STYLES: Record<LocationStatus, { label: string; classes: string }> = {
  healthy: { label: "Healthy", classes: "bg-primary/10 text-primary" },
  "at-risk": { label: "At Risk", classes: "bg-destructive/10 text-destructive" },
  "pre-opening": { label: "Pre-Opening", classes: "bg-accent/10 text-accent" },
};

interface LocationCardProps {
  location: Location;
  isActive: boolean;
  status: LocationStatus;
  score: number;
  compact: boolean;
  onClick: () => void;
}

export default function LocationCard({ location, isActive, status, score, compact, onClick }: LocationCardProps) {
  const s = STATUS_STYLES[status];
  const scoreColor = score >= 80 ? "text-primary" : score >= 60 ? "text-accent" : "text-destructive";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-lg border p-3 transition-all ${
        isActive ? "border-foreground bg-card shadow-sm" : "border-border bg-card hover:border-foreground/20"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${isActive ? "bg-foreground" : "bg-muted"}`}>
          <MapPin className={`w-4 h-4 ${isActive ? "text-background" : "text-muted-foreground"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{location.name}</p>
          {!compact && (
            <p className="text-xs text-muted-foreground truncate">
              {[location.business_type, location.city].filter(Boolean).join(" · ")}
            </p>
          )}
          {compact && location.city && (
            <p className="text-xs text-muted-foreground truncate">{location.city}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${s.classes}`}>{s.label}</span>
          <span className={`text-xs font-medium ${scoreColor}`}>{score}</span>
        </div>
      </div>
    </button>
  );
}
